import React from 'react';
import ParallaxSection from './ParallaxSection';
import SectionHeading from './SectionHeading';
import Button from './Button';
import { Accordion, AccordionItem } from './Accordion';
import { useScrollToContact } from '../hooks/useScrollToContact';

interface PriceItemProps {
  duration: string;
  price: string;
  description?: string;
}

const PriceItem: React.FC<PriceItemProps> = ({ duration, price, description }) => (
  <div className="border-b border-gray-200 py-3">
    <div className="flex justify-between items-center">
      <span className="font-medium">{duration}</span>
      <span className="text-primary font-bold">{price}</span>
    </div>
    {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
  </div>
);

const BecomePilot: React.FC = () => {
  const scrollToContact = useScrollToContact();

  return (
    <ParallaxSection
      id="pilot-werden"
      backgroundImage="/images/Flugsportverein_Bilder_11.jpg"
    >
      <div>
        <SectionHeading
          eyebrow="Ausbildung & Mitfliegen"
          title="Pilot werden"
          subtitle="Entdecken Sie die Faszination des Fliegens und starten Sie Ihre Pilotenkarriere bei uns."
        />

        <div className="mb-10">
          <Accordion>
            <AccordionItem title="Der Weg zum Flugschein">
            <p className="mb-4">
              Der Weg zum Flugschein führt bei uns über eine fundierte theoretische und praktische Ausbildung. 
              Sie können jederzeit mit der Ausbildung beginnen und in Ihrem eigenen Tempo lernen.
            </p>
            
            <h4 className="font-bold mt-4">Angebotene Lizenzen/Berechtigungen:</h4>
            <ul className="mt-2 space-y-2 pl-5 list-none">
              <li>Sportpilotenlizenz (SPL) für Ultraleichtflugzeuge</li>
              <li>Segelflugzeugführerschein (GPL)</li>
            </ul>
            
            <p className="mt-4">
              <strong>Sie können jederzeit mit der Ausbildung beginnen!</strong> Wir bieten regelmäßig Theorieunterricht an und 
              die praktische Ausbildung ist ganzjährig möglich (wetterabhängig).
            </p>
           

            </AccordionItem>
          
            <AccordionItem title="Schnupperflüge & Pilot für einen Tag">
            <p className="mb-4">
              Sie sind sich noch nicht sicher, ob das Fliegen das Richtige für Sie ist? Kein Problem! 
              Wir bieten verschiedene Möglichkeiten, um in die Welt der Luftfahrt hineinzuschnuppern.
            </p>
            
            <h4 className="font-bold mt-4">Schnupperflüge:</h4>
            <p className="mt-2">
              Fliegen Sie mit einem unserer erfahrenen Piloten mit und erleben Sie die Faszination des Fliegens hautnah.
            </p>
            
            <div className="mt-4 bg-gray-50 p-4 rounded-md">
              <PriceItem duration="15 Minuten" price="40 €" />
              <PriceItem duration="30 Minuten" price="75 €" />
            </div>
            
            <h4 className="font-bold mt-6">Pilot für einen Tag:</h4>
            <p className="mt-2">
              Steigen Sie ins Cockpit und übernehmen Sie das Steuer! Ihr Erlebnis beinhaltet:
            </p>
            <ul className="mt-2 space-y-2 pl-5 list-none">
              <li>1 Stunde Theorie und technisches Briefing</li>
              <li>Einführung in das Flugzeug</li>
              <li>Etwa 1 Stunde Flugzeit, in der Sie selbst fliegen</li>
              <li>Nachbesprechung und Auswertung des Fluges</li>
            </ul>
            <p className="mt-2 text-sm italic">Vorsicht: Dieses Erlebnis könnte Ihre Leidenschaft fürs Fliegen entfachen!</p>
            
            <div className="mt-4 bg-gray-50 p-4 rounded-md">
              <PriceItem duration="Pilot für einen Tag" price="230 €" description="Inkl. Theorie, Briefing, Flug und Debriefing, Dauer ca. 3 Stunden" />
            </div>
            </AccordionItem>
          
            <AccordionItem title="Wichtige Informationen">
            <div className="space-y-6">
              <div>
                <h4 className="font-bold">Flugbedingungen:</h4>
                <ul className="mt-2 space-y-2 pl-5 list-none">
                  <li>Unsere Ultraleichtflugzeuge sind für zwei Personen zugelassen</li>
                  <li>Alle Flüge erfolgen unter Sichtflugregeln (VFR)</li>
                  <li>Wettervoraussetzungen: Kein Regen und kein starker oder böiger Wind</li>
                  <li>Der Pilot vor Ort entscheidet über die Durchführung des Fluges</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold">Betriebszeiten:</h4>
                <p className="mt-2">
                  Von 30 Minuten vor Sonnenaufgang bis 30 Minuten nach Sonnenuntergang
                </p>
              </div>
              <div>
                <h4 className="font-bold">Bestellung von Rundflug-Gutscheinen:</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex">
                    <span className="mr-2 font-medium">1.</span>
                    <span>Füllen Sie das Online-Formular auf unserer Website aus</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">2.</span>
                    <span>Wählen Sie die gewünschte Dauer des Rundflugs</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">3.</span>
                    <span>Sie erhalten den Gutschein sowie die Rechnung per E-Mail</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">4.</span>
                    <span>Vereinbaren Sie anschließend einen Termin für Ihren Flug mit uns</span>
                  </div>
                </div>
              </div>
            </div>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="text-center">
          <Button href="#kontakt" onClick={scrollToContact}>
            Jetzt Rundflug buchen oder Ausbildung starten!
          </Button>
        </div>
      </div>
    </ParallaxSection>
  );
};

export default BecomePilot; 